"use client";

import * as React from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
} from "@/components/ui/accordion";
import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

import { EstablishedWhiteSection } from "./section-shell";

interface ChannelLink {
  label: string;
  icon: string;
  url: string;
  external: boolean;
  description?: string;
}

interface EstablishedChannelsProps {
  title: string;
  links: readonly ChannelLink[];
  className?: string;
}

/** Multi-channel links — FAQ accordion (Figma 9027:27537, HomeFaq pattern). */
export function EstablishedChannels({
  title,
  links,
  className,
}: EstablishedChannelsProps) {
  const [openValue, setOpenValue] = React.useState<string | undefined>(
    links[0]?.label
  );

  return (
    <EstablishedWhiteSection
      stack
      dataTestId="established-channels-section"
      className={className}
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:gap-[112px]">
        <h2 className="text-grey-950 lg:text-ds-heading-h3 text-[24px] leading-[1.2] tracking-[-0.96px] lg:w-[260px] lg:shrink-0">
          {title}
        </h2>

        <Accordion
          value={openValue ? [openValue] : []}
          onValueChange={(value) => setOpenValue(value[0])}
          className="flex flex-1 flex-col gap-4 lg:max-w-[780px]"
        >
          {links.map((link) => {
            const open = openValue === link.label;

            return (
              <AccordionItem
                key={link.label}
                value={link.label}
                className="border-grey-100 bg-grey-50 flex flex-col gap-4 rounded-2xl border p-6"
              >
                <AccordionPrimitive.Header className="flex">
                  <AccordionPrimitive.Trigger
                    className={cn(
                      "text-grey-950 text-ds-body-md flex w-full items-center gap-4 text-left outline-none",
                      open && "py-1"
                    )}
                  >
                    <span className="flex-1">{link.label}</span>
                    <span
                      aria-hidden
                      className="border-grey-100 bg-grey-0 text-grey-950 flex size-7 shrink-0 items-center justify-center rounded-full border"
                    >
                      {open ? <MinusIcon /> : <PlusIcon />}
                    </span>
                  </AccordionPrimitive.Trigger>
                </AccordionPrimitive.Header>
                <AccordionContent className="pb-0">
                  {link.description ? (
                    <p className="text-grey-950 text-ds-body-md mb-3">
                      {link.description}
                    </p>
                  ) : null}
                  <Link
                    href={link.url}
                    external={link.external}
                    className="text-pulse-700 text-ds-label-md inline-flex items-center gap-1 font-medium"
                  >
                    Learn more →
                  </Link>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </EstablishedWhiteSection>
  );
}

function PlusIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M6 2.5C6.27614 2.5 6.5 2.72386 6.5 3V5.5H9C9.27614 5.5 9.5 5.72386 9.5 6C9.5 6.27614 9.27614 6.5 9 6.5H6.5V9C6.5 9.27614 6.27614 9.5 6 9.5C5.72386 9.5 5.5 9.27614 5.5 9V6.5H3C2.72386 6.5 2.5 6.27614 2.5 6C2.5 5.72386 2.72386 5.5 3 5.5H5.5V3C5.5 2.72386 5.72386 2.5 6 2.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M9 5.5C9.27614 5.5 9.5 5.72386 9.5 6C9.5 6.27614 9.27614 6.5 9 6.5H3C2.72386 6.5 2.5 6.27614 2.5 6C2.5 5.72386 2.72386 5.5 3 5.5H9Z"
        fill="currentColor"
      />
    </svg>
  );
}
